import { useEffect, useState, useCallback } from 'react'
import { useSessionContext } from '../contexts/SessionContext'
import { supabase } from '../utils/helpers/supabase.helpers'
import { AgamiExperience } from '../types/supabase'
import { Capability } from '../utils/constants/capabilities.constants'

/**
 * An experience as served by `get-user-experiences`: the full experience row plus
 * the caller's role (as a slug, e.g. 'owner' or 'listener') and the capabilities
 * computed server-side.
 */
export type AccessibleExperience = AgamiExperience & {
  role: string | null
  capabilities: Capability[]
}

interface UseAccessibleExperiencesOptions {
  enabled?: boolean
}

interface UseAccessibleExperiencesReturn {
  experiences: AccessibleExperience[] | null
  isLoading: boolean
  refetch: () => Promise<void>
}

/**
 * Every experience the user can reach — owned AND shared with them.
 *
 * Deliberately a separate hook from `useUserExperiences`, which answers a different
 * question (owned only) for views where shared experiences would be invalid targets
 * (patterns, access codes). Routing between the two happens in the view, on the
 * `enabled` option, so neither hook needs to know about the other.
 */
export const useAccessibleExperiences = (
  options: UseAccessibleExperiencesOptions = {}
): UseAccessibleExperiencesReturn => {
  const { user } = useSessionContext()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [experiences, setExperiences] = useState<AccessibleExperience[] | null>(null)
  const { enabled = true } = options

  const fetchExperiences = useCallback(async (): Promise<void> => {
    if (enabled === false) {
      return
    }

    const { data, error } = await supabase.functions.invoke('get-user-experiences')

    if (error || !data) {
      setIsLoading(false)
      return
    }

    setExperiences(data)
    setIsLoading(false)
  }, [enabled])

  useEffect(() => {
    if (!user || enabled === false) {
      return
    }

    fetchExperiences()
  }, [user, enabled, fetchExperiences])

  return { experiences, isLoading, refetch: fetchExperiences }
}
